import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { StaffKpi, StaffMember } from "@/components/staff/staffTypes";
import type { StaffListResponse } from "@/actions/ssr/staff/getStaffListSSR";

interface StaffState {
  results: StaffMember[];
  count: number;
  total_pages: number;
  current_page: number;
  allCount: number;
  activeCount: number;
  inactiveCount: number;
  onLeaveCount: number;
  allStaff: StaffMember[];
  kpi: StaffKpi;
  staffDateFilter: { from: string | null; to: string | null };
  listClientFetched: boolean;
  kpiClientFetched: boolean;
}

const initialState: StaffState = {
  results: [],
  count: 0,
  total_pages: 0,
  current_page: 1,
  allCount: 0,
  activeCount: 0,
  inactiveCount: 0,
  onLeaveCount: 0,
  allStaff: [],
  kpi: {
    totalStaff: 0,
    activeToday: 0,
    percentageOnShift: 0,
    onLeave: 0,
    onLeaveTrendPct: 0,
    onLeaveTrendPositive: false,
    newThisMonth: 0,
    recentlyJoinedLast7Days: 0,
  },
  staffDateFilter: { from: null, to: null },
  listClientFetched: false,
  kpiClientFetched: false,
};

const staffSlice = createSlice({
  name: "staff",
  initialState,
  reducers: {
    setStaffList(state, action: PayloadAction<StaffListResponse>) {
      state.listClientFetched = true;
      state.results = action.payload.results;
      state.count = action.payload.count;
      state.total_pages = action.payload.total_pages;
      state.current_page = action.payload.current_page;
      state.allCount = action.payload.allCount;
      state.activeCount = action.payload.activeCount;
      state.inactiveCount = action.payload.inactiveCount;
      state.onLeaveCount = action.payload.onLeaveCount;
    },
    setStaffListFromSSR(state, action: PayloadAction<StaffListResponse>) {
      if (state.listClientFetched) return;
      state.results = action.payload.results;
      state.count = action.payload.count;
      state.total_pages = action.payload.total_pages;
      state.current_page = action.payload.current_page;
      state.allCount = action.payload.allCount;
      state.activeCount = action.payload.activeCount;
      state.inactiveCount = action.payload.inactiveCount;
      state.onLeaveCount = action.payload.onLeaveCount;
    },
    setAllStaff(state, action: PayloadAction<StaffMember[]>) {
      state.allStaff = action.payload;
    },
    setStaffKpi(state, action: PayloadAction<StaffKpi>) {
      state.kpiClientFetched = true;
      state.kpi = action.payload;
    },
    setStaffKpiFromSSR(state, action: PayloadAction<StaffKpi>) {
      if (state.kpiClientFetched) return;
      state.kpi = action.payload;
    },
    setStaffDateFilter(
      state,
      action: PayloadAction<{ from: string | null; to: string | null }>,
    ) {
      state.staffDateFilter = action.payload;
    },
    resetStaffList(state) {
      state.results = [];
      state.count = 0;
      state.total_pages = 0;
      state.current_page = 1;
      state.allCount = 0;
      state.activeCount = 0;
      state.inactiveCount = 0;
      state.onLeaveCount = 0;
      state.listClientFetched = false;
    },
  },
});

export const {
  setStaffList,
  setStaffListFromSSR,
  setAllStaff,
  setStaffKpi,
  setStaffKpiFromSSR,
  setStaffDateFilter,
  resetStaffList,
} = staffSlice.actions;
export default staffSlice.reducer;
